/** Entering a lice count, one question at a time.
 *
 *  WHY THIS IS NOT A FORM. The previous version was six labelled boxes in a
 *  column, which is a form in the sense that a tax return is a form: correct,
 *  complete, and asking you to hold the whole thing in your head at once before
 *  you have answered any of it. Somebody standing on a quay with a counting
 *  tray and cold hands should never have to decide what to look at next.
 *
 *  So: one question per screen, and the question is the largest thing on it.
 *  Enter advances. A choice advances the moment it is made — no "next" button
 *  to hunt for after you have already answered. The last screen is the whole
 *  count read back, with every line clickable to go and fix it.
 *
 *  THIS IS THE FIRST OVERLAY IN THE PRODUCT, and that is deliberate rather than
 *  a lapse. `Menu.tsx` argues against dialogs and it is right about the case it
 *  covers: a destructive confirm on a list row is an interruption, and covering
 *  the row you were looking at is exactly wrong. This is the opposite case —
 *  entering a count is not an interruption of the page, it *is* the task, it
 *  was reached by pressing a button that says so, and taking the screen is what
 *  lets each question be asked properly. It traps focus, restores it on close,
 *  answers Escape, and locks the scroll behind it.
 *
 *  Nothing here is decoration. The order is the order a person counts in: which
 *  day, what you did to the fish, then what you found, coarse to fine.
 */
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  ApiError, addLiceCount,
  type LiceCheck, type LiceCount, type LiceTreatment, type Metode,
} from '../api'
import { isoUke, nb } from './Varsel'
import { useT } from '../lang'
import { today as todayKey } from '../time'

export const METODER: { key: Metode; navn: string }[] = [
  { key: 'kaldtvann', navn: 'Kaldtvannsbad' },
  { key: 'ferskvann', navn: 'Ferskvann' },
  { key: 'medikament', navn: 'Medikamentell' },
  { key: 'mekanisk', navn: 'Mekanisk' },
  { key: 'termisk', navn: 'Termisk' },
]

export const metodeNavn = (m: Metode) =>
  METODER.find((x) => x.key === m)?.navn ?? m

const today = () => new Date().toISOString().slice(0, 10)

export function datoLang(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const iAar = d.getUTCFullYear() === new Date().getUTCFullYear()
  return d.toLocaleDateString('nb-NO',
    { day: 'numeric', month: 'long', ...(iAar ? {} : { year: 'numeric' }) })
}

/** "Uke 33 · tirsdag 11. august". The weekday is spelled out because a farmer
 *  knows which day they counted long before they know the date. */
function ukeOgDag(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `Uke ${isoUke(iso)} · ${d.toLocaleDateString('nb-NO',
    { weekday: 'long', day: 'numeric', month: 'long' })}`
}

/** dd/mm/yyyy. The one place a date is shown as digits rather than as words. */
export function ddmmyyyy(iso: string): string {
  const [y, m, d] = iso.split('-')
  return d && m && y ? `${d}/${m}/${y}` : ''
}

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${
    String(d.getDate()).padStart(2, '0')}`

/** The calendar, drawn by us.
 *
 *  `<input type="date">` was three problems wearing one coat: it prints the
 *  date in whatever order the browser's locale fancies, its trigger sits on the
 *  right while the panel opens hard left, and none of it can be styled — so a
 *  page built carefully in the Phoniq palette handed over to a grey operating
 *  system widget at the one moment it was asking for something.
 *
 *  On a screen whose whole job is one question, the answer is not to restyle a
 *  popup but to delete it: the calendar IS the screen. Nothing to open, nothing
 *  to anchor, nothing to misalign.
 *
 *  Monday first, which is the week Norway and ISO 8601 both use, and the week
 *  number down the side because the forecast is written in weeks and this is
 *  where a person decides which week they are reporting.
 */
export function Kalender({ value, max, onPick }: {
  value: string; max: string; onPick: (d: string) => void
}) {
  const sel = new Date(`${value}T00:00:00`)
  const valid = !Number.isNaN(sel.getTime())
  const base = valid ? sel : new Date()
  const [vis, setVis] = useState(new Date(base.getFullYear(), base.getMonth(), 1))
  // From time.ts rather than the local clock, so that "today" is marked on the
  // day the rest of the page is counting in. The cells themselves stay local
  // Date arithmetic: those are calendar squares, not instants.
  const today = todayKey()

  const first = new Date(vis.getFullYear(), vis.getMonth(), 1)
  // Monday-first: getDay() is Sunday-first, so Sunday becomes 6 rather than 0.
  const lead = (first.getDay() + 6) % 7
  const days = new Date(vis.getFullYear(), vis.getMonth() + 1, 0).getDate()

  const cells: (string | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: days }, (_, n) =>
      iso(new Date(vis.getFullYear(), vis.getMonth(), n + 1))),
  ]
  while (cells.length % 7) cells.push(null)
  const weeks = Array.from({ length: cells.length / 7 },
    (_, w) => cells.slice(w * 7, w * 7 + 7))

  const shift = (n: number) =>
    setVis(new Date(vis.getFullYear(), vis.getMonth() + n, 1))
  // Nothing ahead of today is reachable, so neither is the month it sits in.
  const aheadOfMax = new Date(vis.getFullYear(), vis.getMonth(), 1)
    > new Date(`${max}T00:00:00`)

  return (
    <div className="kal">
      <div className="kal-topp">
        <button type="button" className="tf-icon" onClick={() => shift(-1)}
          aria-label="Forrige måned">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            strokeLinejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <strong>
          {vis.toLocaleDateString('nb-NO', { month: 'long', year: 'numeric' })}
        </strong>
        <button type="button" className="tf-icon" onClick={() => shift(1)}
          disabled={aheadOfMax} aria-label="Neste måned">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            strokeLinejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>

      <table className="kal-rutenett">
        <thead>
          <tr>
            <th aria-label="Uke">U</th>
            {['ma', 'ti', 'on', 'to', 'fr', 'lø', 'sø'].map((d) => (
              <th key={d}>{d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week.find(Boolean) ?? String(week)}>
              <td className="kal-uke">
                {(() => { const d = week.find(Boolean); return d ? isoUke(d) : '' })()}
              </td>
              {week.map((d, n) => (
                <td key={d ?? `x${n}`}>
                  {d && (
                    <button type="button"
                      className={`kal-dag${d === value ? ' valgt' : ''}${
                        d === today ? ' idag' : ''}`}
                      disabled={d > max}
                      // The day already chosen holds the caret, so arrowing
                      // and tabbing both start from where you are.
                      autoFocus={d === value}
                      aria-current={d === value ? 'date' : undefined}
                      onClick={() => onPick(d)}>
                      {Number(d.slice(8))}
                    </button>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

type StepId =
  | 'dato' | 'avluset' | 'avlusing_dato' | 'metode' | 'dekning'
  | 'voksne' | 'bevegelige' | 'fastsittende' | 'fisk' | 'temp' | 'notat'
  | 'kvitter'

export function Telling({
  siteId, initialDate, counts, treatments, windowEnd, origin, grense, tempHint,
  onSaved, onClose,
}: {
  siteId: string
  initialDate: string
  counts: LiceCount[]
  treatments: LiceTreatment[]
  windowEnd: string | null
  origin: string | null
  grense: number | null
  /** A starting figure for the sea temperature, and where it came from. An
   *  empty field asks somebody to produce a number; a filled one asks them to
   *  confirm or correct it, which is much less work and much likelier to
   *  happen. Never silently: the source is named under the field, because a
   *  guess presented as a measurement is worse than no guess. */
  tempHint: { value: number; kilde: string } | null
  onSaved: (items: LiceCount[], check: LiceCheck | null,
            observed: number | null,
            treatment: { treated_at: string; method: Metode } | null) => void
  onClose: () => void
}) {
  const t = useT()
  const max = today()

  const [date, setDate] = useState(initialDate)
  const [deloused, setDeloused] = useState<boolean | null>(null)
  const [treatDate, setTreatDate] = useState('')
  const [method, setMethod] = useState<Metode>('kaldtvann')
  const [coverage, setCoverage] = useState('1')
  const [adult, setAdult] = useState('')
  const [mobile, setMobile] = useState('')
  const [sessile, setSessile] = useState('')
  const [fish, setFish] = useState('')
  const [temp, setTemp] = useState('')
  const [note, setNote] = useState('')

  const [i, setI] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [leaving, setLeaving] = useState(false)
  /* The count that was just saved, and what it did to the forecast. Holding it
     HERE rather than handing it to the page is the whole fix: as a card behind
     the overlay it was something you had to notice, and it got scrolled past.
     The last screen of the flow cannot be missed, because it is the screen you
     are standing on. */
  const [done, setDone] = useState<
    { items: LiceCount[]; check: LiceCheck | null; observed: number | null
      treatment: { treated_at: string; method: Metode } | null } | null>(null)

  /* Re-seed from the row for the chosen day. Picking a day that already has a
     count makes this a correction of it rather than a second count — the server
     has always upserted on (site, date), and this is where that becomes
     something a person can see. */
  const existing = counts.find((c) => c.counted_at === date) ?? null
  const [seeded, setSeeded] = useState<string | null>(null)
  if (seeded !== date) {
    setSeeded(date)
    const s = (v: number | null | undefined) => (v == null ? '' : String(v))
    setAdult(s(existing?.adult_female))
    setMobile(s(existing?.mobile))
    setSessile(s(existing?.sessile))
    setFish(s(existing?.n_fish))
    // A reading already on this day wins; otherwise offer the hint, which is
    // their own last measurement or, failing that, the register's.
    setTemp(existing?.temp_c != null ? String(existing.temp_c)
      : tempHint ? String(tempHint.value) : '')
    setNote(existing?.note ?? '')
    // Do not ask what we already know: a delousing already on record inside the
    // forecast's window is pre-answered, and they confirm rather than type.
    const known = treatments.find(
      (x) => origin && x.treated_at > origin && x.treated_at <= date)
    if (known) {
      setDeloused(true)
      setTreatDate(known.treated_at)
      setMethod(known.method)
      setCoverage(String(known.coverage))
    }
  }

  const steps: StepId[] = [
    'dato', 'avluset',
    ...(deloused ? (['avlusing_dato', 'metode', 'dekning'] as StepId[]) : []),
    'voksne', 'bevegelige', 'fastsittende', 'fisk', 'temp', 'notat', 'kvitter',
  ]
  const step = steps[Math.min(i, steps.length - 1)]!
  const at = steps.indexOf(step)

  const next = () => setI(Math.min(at + 1, steps.length - 1))
  const back = () => setI(Math.max(at - 1, 0))
  const goTo = (id: StepId) => setI(Math.max(steps.indexOf(id), 0))

  const adultN = tall(adult)
  const touched = [adult, mobile, sessile, fish, temp, note]
    .some((v) => v.trim() !== '')
    || deloused !== null

  const wrap = useRef<HTMLDivElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)

  /* Take the screen properly: remember what had focus, stop the page behind
     from scrolling, answer Escape, and keep Tab inside. An overlay that leaks
     focus to the page underneath is worse than no overlay. */
  useEffect(() => {
    returnTo.current = document.activeElement as HTMLElement | null
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setLeaving(true); return }
      if (e.key !== 'Tab' || !wrap.current) return
      const live = wrap.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      if (!live.length) return
      const first = live[0]!, last = live[live.length - 1]!
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKey, true)
      returnTo.current?.focus?.()
    }
  }, [])

  // The one control that matters on this screen gets the caret, every time.
  const field = useRef<HTMLInputElement | HTMLButtonElement | null>(null)
  useEffect(() => { field.current?.focus() }, [i, step])

  const save = async () => {
    setBusy(true); setError(null)
    const treatment = deloused && treatDate
      ? { treated_at: treatDate, method, coverage: Number(coverage) }
      : null
    try {
      const r = await addLiceCount(siteId, {
        counted_at: date,
        adult_female: adultN,
        mobile: tall(mobile),
        sessile: tall(sessile),
        n_fish: tall(fish),
        temp_c: tall(temp),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(treatment ? { treatment } : {}),
      })
      // The page reloads the forecast NOW, behind this screen, so by the time
      // they press through the strip has already redrawn.
      onSaved(r.items, r.check, adultN, treatment)
      setDone({ items: r.items, check: r.check, observed: adultN, treatment })
      setBusy(false)
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.code)
        : 'Kom ikke i kontakt med mottakeren.')
      setBusy(false)
    }
  }

  const over = grense != null && adultN != null && adultN >= grense
  const beforeWindow = !!windowEnd && date <= windowEnd

  if (done) {
    return (
      <div className="tf" role="dialog" aria-modal="true"
        aria-label="Slik traff varselet" ref={wrap}>
        <Ferdig {...done} date={date} onClose={onClose} />
      </div>
    )
  }

  return (
    <div className="tf" role="dialog" aria-modal="true"
      aria-label={t('Add a lice count')} ref={wrap}>
      <div className="tf-bar" aria-hidden="true">
        <span style={{ width: `${((at + 1) / steps.length) * 100}%` }} />
      </div>

      <div className="tf-top">
        <button type="button" className="tf-icon" onClick={back}
          disabled={at === 0} aria-label={t('Back')}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            strokeLinejoin="round" aria-hidden="true">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <span className="tf-count">{at + 1} {t('of')} {steps.length}</span>
        <button type="button" className="tf-icon"
          onClick={() => (touched ? setLeaving(true) : onClose())}
          aria-label={t('Close')}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      <div className="tf-body">
        {/* Keyed on the step so each one animates in as its own thing. */}
        <div className="tf-step" key={step}>
          {step === 'dato' && (
            <Ask q={t('When did you count?')}
              hint={`${ddmmyyyy(date)} · ${ukeOgDag(date)}`}
              onNext={next}>
              <Kalender value={date} max={max}
                onPick={(d) => { setDate(d); setI(at + 1) }} />
              {existing && (
                <p className="tf-merk">
                  {t('There is already a count for this day')}
                  {existing.counted_by && <> — {t('entered by')} {existing.counted_by}</>}.{' '}
                  {t('Saving replaces it. The previous numbers are kept in the record.')}
                </p>
              )}
              {beforeWindow && !existing && (
                <p className="tf-merk">
                  {t("This day falls inside the register's own window, which closes")}{' '}
                  {datoLang(windowEnd!)}.{' '}
                  {t('The count is stored, but the forecast keeps building on the register.')}
                </p>
              )}
            </Ask>
          )}

          {/* ASKED BEFORE THE NUMBERS, because it changes what they mean. The
              forecast only ever claimed what would happen if nobody deloused. */}
          {step === 'avluset' && (
            <Ask q={origin
              ? `${t('Have you deloused since')} ${datoLang(origin)}?`
              : `${t('Have you deloused')}?`}
              hint={t('The forecast only says what happens if you do not. If you did, it is not a test of it.')}>
              <Valg options={[
                { value: 'nei', label: t('No') },
                { value: 'ja', label: t('Yes') },
              ]} value={deloused === null ? '' : deloused ? 'ja' : 'nei'}
                first={field as never}
                onPick={(v) => {
                  const yes = v === 'ja'
                  setDeloused(yes)
                  if (yes && !treatDate) setTreatDate(date)
                  setI(at + 1)
                }} />
            </Ask>
          )}

          {step === 'avlusing_dato' && (
            <Ask q={t('When did you delouse?')}
              hint={`${ddmmyyyy(treatDate)} · ${ukeOgDag(treatDate)}`}
              onNext={next}>
              <Kalender value={treatDate} max={max}
                onPick={(d) => { setTreatDate(d); setI(at + 1) }} />
            </Ask>
          )}

          {step === 'metode' && (
            <Ask q={t('Which method?')}
              hint={t('Each one knocks the three stages down differently, and the model knows by how much.')}>
              <Valg options={METODER.map((m) => ({ value: m.key, label: m.navn }))}
                value={method} first={field as never}
                onPick={(v) => { setMethod(v as Metode); setI(at + 1) }} />
            </Ask>
          )}

          {step === 'dekning' && (
            <Ask q={t('How much of the site?')}
              hint={t('A partial delousing that halves the average is not the same event as one that empties it.')}>
              <Valg options={[
                { value: '1', label: t('All cages') },
                { value: '0.5', label: t('Some cages') },
              ]} value={coverage} first={field as never}
                onPick={(v) => { setCoverage(v); setI(at + 1) }} />
            </Ask>
          )}

          {step === 'voksne' && (
            <Ask q={t('How many adult female lice per fish?')} onNext={next}>
              <Tall ref={field as never} value={adult} onChange={setAdult}
                placeholder="0,00" />
              {over && (
                <p className="tf-over">
                  {nb(adultN!)} {t('is over the limit of')} {nb(grense!)}{' '}
                  {t('for week')} {isoUke(date)}.
                </p>
              )}
            </Ask>
          )}

          {step === 'bevegelige' && (
            <Ask q={t('And mobile lice?')}
              hint={t('Blank if you did not count them. Blank is not zero.')}
              onNext={next} skip={mobile.trim() === ''}>
              <Tall ref={field as never} value={mobile} onChange={setMobile}
                placeholder="0,00" />
            </Ask>
          )}

          {step === 'fastsittende' && (
            <Ask q={t('And sessile lice?')}
              hint={t('These become adult females in two to three weeks. Without them the forecast is measurably worse.')}
              onNext={next} skip={sessile.trim() === ''}>
              <Tall ref={field as never} value={sessile} onChange={setSessile}
                placeholder="0,00" />
            </Ask>
          )}

          {step === 'fisk' && (
            <Ask q={t('How many fish did you count?')}
              hint={t('Without this the model assumes 100. Ten is the legal minimum, and not enough to tell 0,10 from 0,20 on one count.')}
              onNext={next} skip={fish.trim() === ''}>
              <Tall ref={field as never} value={fish} onChange={setFish}
                placeholder="20" heltall />
            </Ask>
          )}

          {step === 'temp' && (
            <Ask q={t('How warm is the sea?')}
              hint={tempHint && temp === String(tempHint.value)
                ? tempHint.kilde
                : t('At three metres, where the lice are counted.')}
              onNext={next} skip={temp.trim() === ''}>
              <Tall ref={field as never} value={temp} onChange={setTemp}
                placeholder="11,4" suffix="°C" />
            </Ask>
          )}

          {step === 'notat' && (
            <Ask q={t('Anything worth noting?')}
              hint={t('Optional. What else happened that week.')}
              onNext={next} skip={note.trim() === ''}>
              <input ref={field as never} type="text" className="tf-in tf-tekst"
                maxLength={500} value={note}
                placeholder={t('counted in cages 3 and 5')}
                onChange={(e) => setNote(e.target.value)} />
            </Ask>
          )}

          {step === 'kvitter' && (
            <Kvitter
              date={date} deloused={deloused} treatDate={treatDate}
              method={method} coverage={coverage} adult={adult} mobile={mobile}
              sessile={sessile} fish={fish} temp={temp} note={note} over={over}
              grense={grense} existing={!!existing} busy={busy} error={error}
              first={field as never} onEdit={goTo} onSave={save} />
          )}
        </div>
      </div>

      {leaving && (
        <div className="tf-forlate">
          <div>
            <strong>{t('Discard this count?')}</strong>
            <p>{t('Nothing has been saved yet.')}</p>
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn danger sm" onClick={onClose}>
                {t('Discard it')}
              </button>
              <button className="btn ghost sm" autoFocus
                onClick={() => setLeaving(false)}>
                {t('Keep going')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** One question, its answer, and the way onward. `onNext` present means the
 *  answer is typed, so Enter submits; absent means the answer is a choice and
 *  picking it is the whole action. */
function Ask({ q, hint, children, onNext, skip }: {
  q: string; hint?: string; children: ReactNode
  onNext?: () => void; skip?: boolean
}) {
  const t = useT()
  return (
    <form onSubmit={(e) => { e.preventDefault(); onNext?.() }}>
      <h2 className="tf-q">{q}</h2>
      {hint && <p className="tf-hint">{hint}</p>}
      {children}
      {onNext && (
        <div className="tf-neste">
          <button className="btn primary" type="submit">
            {skip ? t('Skip') : t('Next')}
          </button>
          {/* Shown because it is true and because it is how this gets fast:
              type, Enter, type, Enter. Hidden where there is no keyboard. */}
          <span className="tf-tast">{t('or press')} <kbd>↵</kbd></span>
        </div>
      )}
    </form>
  )
}

/** A big, plain number, typed the way a Norwegian types one.
 *
 *  NOT `type="number"`. That control accepts only the browser's own decimal
 *  separator, which on most of these machines is a full stop — so the field
 *  showed a placeholder of `0,00` and then silently refused the comma, which is
 *  the key everybody here reaches for. A placeholder that demonstrates an input
 *  the field rejects is worse than no placeholder.
 *
 *  So it is a text field that accepts both and normalises on the way out. It
 *  also loses the spinners for free, which is a mercy: a stepper on a lice
 *  average invites somebody to arrive at 0,37 by pressing an arrow
 *  thirty-seven times. */
function Tall({ ref, value, onChange, placeholder, heltall, suffix }: {
  ref: React.Ref<HTMLInputElement>
  value: string; onChange: (v: string) => void
  placeholder?: string; heltall?: boolean; suffix?: string
}) {
  const felt = (
    <input ref={ref} type="text" className="tf-in tf-tall"
      inputMode="decimal" autoComplete="off"
      value={value} placeholder={placeholder}
      onChange={(e) => {
        // Digits, and one separator of either kind. Anything else never
        // reaches the field, so there is nothing to reject later.
        const v = e.target.value.replace(heltall ? /[^\d]/g : /[^\d.,]/g, '')
        onChange(heltall ? v : v.replace(/([.,].*?)[.,]/g, '$1'))
      }} />
  )
  if (!suffix) return felt
  return (
    <span className="tf-med-enhet">
      {felt}
      <span className="tf-enhet" aria-hidden="true">{suffix}</span>
    </span>
  )
}

/* Both separators in, a number out. Norwegian keyboards give a comma. */
export const tall = (v: string): number | null => {
  if (v.trim() === '') return null
  const n = Number(v.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

/** Two choices or five, as buttons big enough to hit with a glove on. Picking
 *  one IS the action — there is no separate "next" to find afterwards. */
function Valg({ options, value, onPick, first }: {
  options: { value: string; label: string }[]
  value: string
  onPick: (v: string) => void
  first: React.Ref<HTMLButtonElement>
}) {
  /* The badges say 1, 2, 3. Printing a keyboard hint that does nothing is
     worse than printing none, so the digits are wired here — and this is what
     keeps the whole flow on the keyboard: type, Enter, type, 1, Enter. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const n = Number(e.key)
      if (!Number.isInteger(n) || n < 1 || n > options.length) return
      e.preventDefault()
      onPick(options[n - 1]!.value)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [options, onPick])

  return (
    <div className="tf-valg">
      {options.map((o, n) => (
        <button key={o.value} type="button"
          ref={n === 0 ? first : undefined}
          className={`tf-valg-en${value === o.value ? ' valgt' : ''}`}
          onClick={() => onPick(o.value)}>
          <span className="tf-tast-hint" aria-hidden="true">{n + 1}</span>
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** The whole count read back before it is saved.
 *
 *  Every line is a button back to the question that produced it, so fixing the
 *  one you got wrong costs a click rather than a walk back through the flow.
 *  This is the screen that makes one-question-at-a-time safe: you never lose
 *  sight of the whole thing, you just stop being shown it while you answer. */
function Kvitter({
  date, deloused, treatDate, method, coverage, adult, mobile, sessile, fish,
  temp, note, over, grense, existing, busy, error, first, onEdit, onSave,
}: {
  date: string; deloused: boolean | null; treatDate: string; method: Metode
  coverage: string; adult: string; mobile: string; sessile: string
  fish: string; temp: string; note: string; over: boolean; grense: number | null
  existing: boolean; busy: boolean; error: string | null
  first: React.Ref<HTMLButtonElement>
  onEdit: (id: StepId) => void
  onSave: () => void
}) {
  const t = useT()
  const val = (v: string) => { const n = tall(v); return n == null ? null : nb(n) }
  const rows: { id: StepId; k: string; v: ReactNode }[] = [
    { id: 'dato', k: t('Counted'), v: `${ddmmyyyy(date)} · ${ukeOgDag(date)}` },
    { id: 'avluset', k: t('Deloused'),
      v: deloused
        ? `${datoLang(treatDate)} · ${metodeNavn(method).toLowerCase()}${
            coverage === '1' ? '' : ` · ${t('Some cages').toLowerCase()}`}`
        : t('No') },
    { id: 'voksne', k: t('Adult female'), v: val(adult) },
    { id: 'bevegelige', k: t('Mobile'), v: val(mobile) },
    { id: 'fastsittende', k: t('Sessile'), v: val(sessile) },
    { id: 'fisk', k: t('Fish'), v: fish.trim() === '' ? null : fish },
    { id: 'temp', k: t('Sea temperature'),
      v: val(temp) == null ? null : `${val(temp)} °C` },
    { id: 'notat', k: t('Note'), v: note.trim() || null },
  ]

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave() }}>
      <h2 className="tf-q">
        {existing ? t('Save this correction?') : t('Does this look right?')}
      </h2>
      <p className="tf-hint">{t('Tap a line to change it.')}</p>

      <dl className="tf-kvitt">
        {rows.map((r, n) => (
          <button key={r.id} type="button" className="tf-kvitt-rad"
            ref={n === 0 ? first : undefined}
            onClick={() => onEdit(r.id)}>
            <dt>{r.k}</dt>
            <dd className={r.v == null ? 'tom' : undefined}>
              {r.v ?? t('not counted')}
            </dd>
          </button>
        ))}
      </dl>

      {/* Not a gate and not a confirmation. It is their number and we do not
          argue with it; we make sure they meant it. */}
      {over && (
        <p className="tf-over">
          {nb(tall(adult)!)} {t('is over the limit of')} {nb(grense!)}{' '}
          {t('for week')} {isoUke(date)}.
        </p>
      )}

      {error && <div className="notice bad" style={{ marginTop: 16 }}>{error}</div>}

      <div className="tf-neste">
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? t('Saving the count…')
            : existing ? t('Save the correction')
              : t('Save and re-run the forecast')}
        </button>
      </div>
    </form>
  )
}


/* ------------------------------------------------------------- the reward */

/** Monday of the week a date falls in. Comparing Mondays rather than ISO
 *  week-years sidesteps the new-year edge cases entirely. */
function mandag(d: string): string {
  const x = new Date(`${d}T00:00:00`)
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7))
  return iso(x)
}

/** Consecutive weeks with a count, ending in this one.
 *
 *  THE STREAK COUNTS WEEKS COUNTED, NEVER THE NUMBER FOUND. That distinction
 *  is the whole ethics of putting a reward on this screen. Rewarding a low
 *  count would put a thumb on a regulated figure that a treatment decision and
 *  a public register both rest on — it would be paying people, in small
 *  dopamine, to under-report. Rewarding the act of counting pushes the only
 *  direction that is safe to push: count, weekly, and tell us honestly.
 *
 *  It also happens to be the thing the farm must do anyway, and the thing the
 *  forecast is worth nothing without. */
function ukesrekke(counts: LiceCount[], from: string): number {
  const weeks = new Set(counts.map((c) => mandag(c.counted_at)))
  const cur = new Date(`${mandag(from)}T00:00:00`)
  let n = 0
  while (weeks.has(iso(cur))) { n += 1; cur.setDate(cur.getDate() - 7) }
  return n
}

/** Where the count landed relative to the forecast.
 *
 *  Signed, and measured in units of the half-band ON THE SIDE IT FELL -- the
 *  interval is not symmetric (0,17 · 0,30 · 0,46 has a short lower half and a
 *  long upper one), and pretending it is would flatter a count on the wide
 *  side. So ±1 is exactly p10 or p90, whichever the count is nearer, and 0 is
 *  the anslag itself.
 */
function avvik(check: LiceCheck | null, observed: number | null): number | null {
  if (!check || observed == null || check.p10 == null || check.p90 == null) return null
  const half = observed < check.central
    ? check.central - check.p10
    : check.p90 - check.central
  if (!(half > 0)) return null
  return (observed - check.central) / half
}

/** Thirds of the band.
 *
 *  Landing inside an eighty per cent interval is what should happen four times
 *  in five, so "innenfor" on its own is close to a non-claim -- the grade has
 *  to say how close. That used to be explained in a paragraph. It is not
 *  explained at all now: the band is drawn in thirds and the count sits where
 *  it sits, which shows the same thing in the time it takes to look.
 */
const baand = (d: number) => {
  const x = Math.abs(d)
  return x <= 1 / 3 ? 'blink' : x <= 2 / 3 ? 'tett' : x <= 1 ? 'kant' : 'bom'
}

/** The dart words, about the MODEL's throw and never the farm's count. The
 *  forecast guessed where the answer would be; the count is where it actually
 *  was. Keeping the judgement on the model is what stops a wide count from
 *  reading as somebody having done something wrong. */
const TITTEL: Record<string, string> = {
  blink: 'Blink.',
  tett: 'Tett på.',
  kant: 'Så vidt innenfor.',
  bom: 'Bom.',
  noytral: 'Lagret.',
}

/** The interval, and where the count fell on it. One mark, not a chart. */
function Spenn({ lo, hi, central, observed }: {
  lo: number; hi: number; central: number; observed: number
}) {
  const span = Math.max(hi - lo, 0.001)
  const from = Math.min(lo, observed) - span * 0.25
  const to = Math.max(hi, observed) + span * 0.25
  const at = (v: number) => ((v - from) / (to - from)) * 100
  return (
    <div className="spenn" aria-hidden="true">
      <span className="spenn-rule" />
      {/* The band drawn as thirds rather than one flat bar, so the eye can see
          that the middle is the part worth hitting. Each third is measured on
          its own side, because the interval is not symmetric. */}
      {([[1, 'y'], [2 / 3, 'm'], [1 / 3, 'i']] as const).map(([f, cls]) => (
        <span key={cls} className={`spenn-sone s-${cls}`}
          style={{
            left: `${at(central - (central - lo) * f)}%`,
            width: `${at(central + (hi - central) * f)
              - at(central - (central - lo) * f)}%`,
          }} />
      ))}
      <span className="spenn-anslag" style={{ left: `${at(central)}%` }} />
      <span className="spenn-talt" style={{ left: `${at(observed)}%` }} />
      <span className="spenn-lo" style={{ left: `${at(lo)}%` }}>{nb(lo)}</span>
      <span className="spenn-hi" style={{ left: `${at(hi)}%` }}>{nb(hi)}</span>
      <span className="spenn-obs" style={{ left: `${at(observed)}%` }}>{nb(observed)}</span>
    </div>
  )
}

/** The last screens, and the only reward in the product.
 *
 *  TWO SCREENS, ONE MESSAGE EACH — the same rule the questions follow. How the
 *  forecast landed is one thing to know; how consistently this farm counts is
 *  another; and stacking them made a wall nobody read.
 *
 *  Almost no words. An earlier version explained in prose that an eighty per
 *  cent interval contains the count four times in five, and separately that a
 *  ten-fish count settles nothing. Both were true and neither was informative:
 *  the first is what the banded drawing already shows, and the second was a
 *  lecture standing where a reward should be. If we want more than ten fish
 *  counted, that belongs in what gets rewarded, not in a paragraph telling
 *  people off.
 *
 *  WHAT IS BORROWED FROM THE HABIT APPS, AND WHAT IS REFUSED.
 *
 *  Borrowed: the completion screen, which cannot be missed because it is where
 *  you are standing; the streak; and the recent weeks drawn as marks.
 *
 *  Refused, and the refusals matter more:
 *
 *  - No leaderboard between farms. Lice numbers go to a public register and
 *    trigger treatment orders; ranking companies on them is a direct incentive
 *    to under-report, and we would be the ones building it.
 *  - Nothing celebrates a LOW count. The grade is about the forecast's aim,
 *    never about the fish being clean.
 *  - No variable or randomised reward. A treatment decision must never acquire
 *    the rhythm of a slot machine.
 *  - A miss is not drawn as a failure. An honest miss is how the model gets
 *    better, and a farm that felt scolded here would quietly stop entering
 *    counts — which costs us the origin the whole forecast rests on.
 */
function Ferdig({
  items, check, observed, treatment, date, onClose,
}: {
  items: LiceCount[]
  check: LiceCheck | null
  observed: number | null
  treatment: { treated_at: string; method: Metode } | null
  date: string
  onClose: () => void
}) {
  const t = useT()
  const [side, setSide] = useState(0)
  const voided = check?.verdict === 'voided'
  const d = voided ? null : avvik(check, observed)
  const tone = voided ? 'voided' : d == null ? 'noytral' : baand(d)
  const uke = check?.target_week ? isoUke(check.target_week) : null

  if (side === 1) {
    return (
      <div className="fd fd-uker">
        <div className="fd-midt">
          <Uker counts={items} to={date} />
          <button className="btn primary fd-ut" autoFocus onClick={onClose}>
            {t('See the forecast')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`fd fd-${tone}`}>
      <div className="fd-midt">
        <h2 className="fd-tittel">
          {voided ? 'Varselet virket.' : TITTEL[tone]}
        </h2>

        {/* One sentence, two numbers and a week. Everything else the drawing
            below says better than a clause could. */}
        {check && observed != null && (
          <p className="fd-sak">
            {voided ? (
              <>
                Varselet sa <b>{nb(check.central)}</b>
                {uke && <> for uke {uke}</>} om ingenting ble gjort. Dere
                avluset {treatment ? datoLang(treatment.treated_at) : ''} og
                talte <b>{nb(observed)}</b>.
              </>
            ) : check.weeks_ahead > 0 ? (
              <>
                {check.weeks_ahead} {check.weeks_ahead === 1 ? 'uke' : 'uker'}{' '}
                tidligere sa varselet <b>{nb(check.central)}</b>
                {uke && <> for uke {uke}</>}. Dere talte <b>{nb(observed)}</b>.
              </>
            ) : (
              <>
                Varselet sa <b>{nb(check.central)}</b>
                {uke && <> for uke {uke}</>}. Dere talte <b>{nb(observed)}</b>.
              </>
            )}
          </p>
        )}

        {!voided && check?.p10 != null && check.p90 != null && observed != null && (
          <Spenn lo={check.p10} hi={check.p90} central={check.central}
            observed={observed} />
        )}

        <button className="btn primary fd-ut" autoFocus onClick={() => setSide(1)}>
          {t('Next')}
        </button>
      </div>
    </div>
  )
}

/** How consistently this farm counts, drawn rather than said.
 *
 *  Quantity by repetition, never by scaling — the same Neurath rule the
 *  forecast strip draws its lice by. Twelve weeks, one mark each, filled where
 *  a count exists. The gaps ARE the nudge: nothing has to tell anybody they
 *  missed a week, because the missed weeks are visibly missing.
 */
function Uker({ counts, to }: { counts: LiceCount[]; to: string }) {
  const t = useT()
  const weeks = new Set(counts.map((c) => mandag(c.counted_at)))
  const rekke = ukesrekke(counts, to)
  const siste: { key: string; talt: boolean }[] = []
  const cur = new Date(`${mandag(to)}T00:00:00`)
  for (let n = 0; n < 12; n += 1) {
    siste.unshift({ key: iso(cur), talt: weeks.has(iso(cur)) })
    cur.setDate(cur.getDate() - 7)
  }

  return (
    <>
      <p className="uker-tall">
        <strong>{rekke}</strong>
        <span>{rekke === 1 ? t('week counted') : t('weeks in a row')}</span>
      </p>
      <div className="uker" aria-hidden="true">
        {siste.map((w) => (
          <span key={w.key} className={`uker-en${w.talt ? ' talt' : ''}`} />
        ))}
      </div>
    </>
  )
}
